import { Check, CircleHelp, TriangleAlert, X } from "lucide-react";
import { Badge } from "./ui/badge";
import type { CheckName, EvalReport } from "@/lib/types";
import { PopeIcon } from "./pope-icon";
import { useTranslation } from "react-i18next";
import { useHolyModeOptional } from "@/preferences/holy-mode";
import { SacredIcon, type SacredIconName } from "./sacred-icon";

const CHECKS: CheckName[] = ["schema", "tools", "retrieval", "neighbors", "conflict", "rubric"];

export const CHECK_KEY: Record<CheckName, string> = {
  schema: "eval.schema",
  tools: "eval.tools",
  retrieval: "eval.retrieval",
  neighbors: "eval.neighbors",
  conflict: "eval.conflict",
  rubric: "eval.rubric",
};

/** ms-bible ends a cut reach list with "… +N" (RFC-0007 §7). It is a count, not a ref. */
const isCutMarker = (value: string) => value.startsWith("… +");

export function EvalReportView({ report, label }: { report: EvalReport | null; label?: string }) {
  const { t } = useTranslation();
  const { enabled } = useHolyModeOptional();
  const pope = t("pope.name");
  const checkIcons: Record<CheckName, SacredIconName> = {
    schema: "scroll",
    tools: "keys",
    retrieval: "book",
    neighbors: "columns",
    conflict: "scale",
    rubric: "quill",
  };
  const voice = report ? (report.passed ? "eval.popeApprovedCopy" : "eval.popeRejectedCopy") : "eval.popeUnavailableCopy";
  return (
    <section aria-label={label ?? t("eval.region")} className={`eval-report flex flex-col gap-3${enabled ? " eval-report--holy" : ""}`}>
      {enabled ? <p className="eval-report__caption">{t("holy.evalCaption")}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="eval-report__pope-mark"><PopeIcon size={enabled ? 34 : 20} /></span>
        <h2 className="text-foreground text-base font-semibold">{t("eval.title")}</h2>
        {report ? <>
          <Badge tone={report.passed ? "success" : "danger"}>{report.passed ? t("eval.approved") : t("eval.rejected")}</Badge>
          <span className="text-muted text-xs">{t("eval.judgeVersion", { version: report.judgeVersion })}</span>
        </> : <Badge tone="warning">{t("eval.unavailable")}</Badge>}
      </div>
      <p className="text-secondary text-sm">{t(voice, { pope })}</p>
      {report ? <ul className="flex flex-col gap-2">
        {CHECKS.map((name) => {
          const check = report.checks.find((candidate) => candidate.name === name);
          return <li key={name} className="border-border-subtle rounded-lg border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="eval-check__name flex items-center gap-2 text-foreground text-sm font-medium">{check?.passed ? <Check size={16} aria-hidden="true" className="text-[#2f6b21]" /> : check ? <X size={16} aria-hidden="true" className="text-danger" /> : <CircleHelp size={16} aria-hidden="true" className="text-muted" />}{enabled ? <SacredIcon name={checkIcons[name]} size={16} /> : null}{t(CHECK_KEY[name])}</span>
              <Badge tone={check ? (check.passed ? "success" : "danger") : "neutral"}>{check ? (check.passed ? t("eval.passed") : t("eval.failed")) : t("eval.checkUnavailable")}</Badge>
            </div>
            {check && check.details.length > 0 ? (
              <ul className="text-secondary mt-2 list-disc pl-5 text-sm">
                {check.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            ) : null}
          </li>
        })}
      </ul> : null}
      {report?.reach?.length ? (
        <section aria-label={t("eval.reachTitle")} className="border-border-subtle rounded-lg border p-3">
          <h3 className="flex items-center gap-2 text-foreground text-sm font-medium">
            <TriangleAlert size={16} aria-hidden="true" className="text-[#7a6d00]" />
            {t("eval.reachTitle")}
          </h3>
          <p className="text-secondary mt-1 text-sm">{t("eval.reachCopy")}</p>
          <ul className="text-secondary mt-2 list-disc pl-5 text-sm">
            {report.reach.map((warning) => (
              <li key={warning.minion}>
                <span className="text-foreground font-medium">{warning.minion}</span>
                {warning.missing.length > 0 ? (
                  <>
                    {": "}
                    <code>{warning.missing.map((ref) => (isCutMarker(ref) ? ref : `@${ref}`)).join(", ")}</code>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {report && report.reach === undefined ? (
        <section aria-label={t("eval.reachTitle")} className="border-border-subtle rounded-lg border p-3">
          <h3 className="text-foreground text-sm font-medium">{t("eval.reachTitle")}</h3>
          <p className="text-secondary mt-1 text-sm">{t("eval.reachUnchecked")}</p>
        </section>
      ) : null}
    </section>
  );
}
